import gql from 'graphql-tag'

const CART_ITEM_PRODUCT_FRAGMENT = gql`
  fragment CartItemProduct on Product {
    displayName
    englishFullDisplayName
    displaySpecifications
    manufacturerName
    manufacturerProductIdentifier
    nameSlug
    primaryImageId
    productErpIdentifier
    reviewScore
    reviewCount
    maxQuantityPerOrder
    minQuantityPerOrder
    nonReturnable
    internalProduct
    category {
      englishPathSlug
    }
    availability {
      maxAvailableQuantity
      internalEtaStock {
        quantity
        eta
      }
      externalStock {
        leadTime
        quantity
      }
      availabilityDetails {
        formattedDateUtc
        key
      }
      availabilityStatus
      availabilityStatusPresentation
      internalStock {
        quantity
      }
      isAvailableForSale
      IsNotifyEligible
      isPreOrder
      productLifeCycleState
      showAvailabilityQuantities
      showEtaDate
      isIncludedInKit
    }
    price {
      priceExcludingVat
      priceIncludingVat
      originalPriceExcludingVat
      originalPriceIncludingVat
      formatted {
        priceExcludingVat
        priceIncludingVat
      }
      isCampaign
      priceVat
      isBid
    }
    requiredEndUserInformation @include(if: $withRequiredEndUserInformation) {
      label
      lineNumber
      isMandatory
    }
    productType
  }
`

export const CART_ITEM_FRAGMENT = gql`
  ${CART_ITEM_PRODUCT_FRAGMENT}
  fragment CartItem on CartItem {
    id
    productErpIdentifier
    quantity
    isMandatory
    isDependent
    mainProductErpIdentifier
    parentCartItemId
    product {
      ...CartItemProduct
    }
    endUserInformation @include(if: $withRequiredEndUserInformation) {
      value
      lineNumber
      label
    }
    comment
  }
`

export const CART_MUTATION_RESULT = gql`
  ${CART_ITEM_PRODUCT_FRAGMENT}
  fragment CartItemResult on CartItemResult {
    id
    productErpIdentifier
    quantity
    isMandatory
    isDependent
    mainProductErpIdentifier
    parentCartItemId
    comment
    product {
      ...CartItemProduct
    }
  }
`
export const CART_FRAGMENT = gql`
  ${CART_ITEM_FRAGMENT}
  fragment Cart on Cart {
    id
    items {
      ...CartItem
    }
  }
`

export const PRODUCT_FRAGMENT = gql`
  fragment Product on Product {
    id
    productErpIdentifier
    displayName
    englishFullDisplayName
    primaryImageId
    category {
      englishPathSlug
    }
    manufacturerName
    manufacturerProductIdentifier
    displaySpecifications
    oneLiner
    internalProduct
    nameSlug
    energyClass
    energyLabelImageId
    reviewScore
    reviewCount
    isNewProduct
    minQuantityPerOrder
    maxQuantityPerOrder
    showSpecificationsInProductCardTitle
    promotedSpecifications(take: 4) {
      name
      value
    }
    price {
      campaignPercentage
      campaignType
      formatted {
        price
        originalPrice
        priceExcludingVat
        priceIncludingVat
      }
      isBestSeller
      isBid
      isCampaign
      isRecommendedProduct
      price
      priceExcludingVat
      priceIncludingVat
      originalPrice
      priceVat
    }
    availability {
      IsNotifyEligible
      maxAvailableQuantity
      internalEtaStock {
        eta
        quantity
      }
      externalStock {
        leadTime
        quantity
      }
      availabilityDetails {
        formattedDateUtc
        key
      }
      availabilityStatus
      availabilityStatusPresentation
      internalStock {
        quantity
        showAlwaysInStock
      }
      isAvailableForSale
      isPreOrder
      productLifeCycleState
      showAvailabilityQuantities
      showEtaDate
    }
    productType
  }
`

export const PRODUCT_FOR_LIST_FRAGMENT = gql`
  fragment ProductForList on SearchResultProduct {
    id
    productErpIdentifier
    displayName
    englishFullDisplayName
    primaryImageId
    manufacturerName
    manufacturerProductIdentifier
    displaySpecifications
    oneLiner
    internalProduct
    nameSlug
    energyClass
    energyDocumentId
    energyLabelImageId
    reviewScore
    reviewCount
    isNewProduct
    showSpecificationsInProductCardTitle
    promotedSpecifications(take: 4) {
      name
      value
    }
    price {
      campaignPercentage
      campaignType
      formatted {
        price
        originalPrice
        priceExcludingVat
        priceIncludingVat
      }
      isBestSeller
      isBid
      isCampaign
      isRecommendedProduct
      price
      priceExcludingVat
      priceIncludingVat
      originalPrice
      priceVat
    }
    categoryName
    categoryPath
    categoryPathEng
    availability {
      IsNotifyEligible
      maxAvailableQuantity
      internalEtaStock {
        quantity
        eta
      }
      externalStock {
        leadTime
        quantity
      }
      availabilityDetails {
        formattedDateUtc
        key
      }
      availabilityStatus
      availabilityStatusPresentation
      internalStock {
        quantity
        showAlwaysInStock
      }
      isAvailableForSale
      isPreOrder
      productLifeCycleState
      showAvailabilityQuantities
      showEtaDate
    }
    minQuantityPerOrder
  }
`

export const PRODUCT_REFERENCE_FRAGMENT = gql`
  ${PRODUCT_FOR_LIST_FRAGMENT}

  fragment ProductReference on ProductReference {
    id
    ticket
    searchResultProduct {
      ...ProductForList
    }
  }
`

export const PRODUCT_AutoComplete_FRAGMENT = gql`
  ${PRODUCT_FRAGMENT}
  fragment AutocompleteProduct on ProductReference {
    id
    ticket
    searchResultProduct: product {
      ...Product
    }
  }
`

export const PRODUCT_LIST_ITEM_FRAGMENT = gql`
  fragment ProductListItem on ProductListItem {
    id
    productErpIdentifier
    product {
      id
      productErpIdentifier
      displayName
    }
  }
`

export const EXTENDED_PRODUCT_LIST_ITEM_FRAGMENT = gql`
  ${PRODUCT_FRAGMENT}

  fragment ExtendedProductListItem on ProductListItem {
    id
    productErpIdentifier
    product {
      ...Product
    }
    quantity
    comment
  }
`
export const FAVORITE_LIST_ITEM_FRAGMENT = gql`
  ${PRODUCT_LIST_ITEM_FRAGMENT}

  fragment FavoriteListItem on ProductList {
    id
    productListName
    items {
      ...ProductListItem
    }
  }
`

export const EXTENDED_FAVORITE_LIST_ITEM_FRAGMENT = gql`
  ${EXTENDED_PRODUCT_LIST_ITEM_FRAGMENT}

  fragment ExtendedFavoriteListItem on ProductList {
    id
    productListName
    items {
      ...ExtendedProductListItem
    }
  }
`

export const FAVORITES_FRAGMENT = gql`
  ${FAVORITE_LIST_ITEM_FRAGMENT}

  fragment Favorites on Favorites {
    productLists {
      ...FavoriteListItem
    }
  }
`
export const EXTENDED_FAVORITES_FRAGMENT = gql`
  ${EXTENDED_FAVORITE_LIST_ITEM_FRAGMENT}
  ${PRODUCT_REFERENCE_FRAGMENT}

  fragment ExtendedFavorites on Favorites {
    productLists {
      ...ExtendedFavoriteListItem
    }
    productRecommendations @include(if: $enableFavoritesRecommendations) {
      ...ProductReference
    }
  }
`

export const COMPARISONS_FRAGMENT = gql`
  ${PRODUCT_LIST_ITEM_FRAGMENT}

  fragment Comparisons on Comparisons {
    productLists {
      id
      productListName
      items {
        ...ProductListItem
      }
    }
  }
`
